import { INDUSTRIES, INDUSTRY_LABELS, type Industry } from '../../types/account.js';
import type { PortalAudienceRule } from '../../types/portalLibrary.js';
import type { InspirationAdaptFor } from '../../types/portalInspiration.js';
import { matchesAudience, type AudienceAccount } from '../portalLibrary/audience.js';
import { PORTAL_OPERATIONAL_TYPES } from '../portalScope.js';

// PORTAL INSPIRATION - "Adapt for". PURE.
//
// Kyck uploads trending videos that have nothing to do with the client's business. `adaptFor` says which
// kind of business the AI should adapt the trend for. It defaults FROM THE AUDIENCE when the audience
// resolves to exactly one industry, and is otherwise 'none' until staff pick.

export const ADAPT_FOR_OPTIONS: ReadonlyArray<{ value: InspirationAdaptFor; label: string }> = [
  { value: 'none', label: 'Not adapted (show as is)' },
  ...INDUSTRIES.map((i) => ({ value: i as InspirationAdaptFor, label: INDUSTRY_LABELS[i] })),
];

export function isAdaptFor(v: unknown): v is InspirationAdaptFor {
  return v === 'none' || (typeof v === 'string' && (INDUSTRIES as readonly string[]).includes(v));
}

/**
 * The industry the audience points at, or 'none'. ⚠️ Resolved from the ACCOUNTS the audience reaches (the same
 * matcher that decides visibility), so a marque rule or an account rule gives the industry of what it names,
 * and "everyone" or a mixed audience - which reaches several industries - gives 'none' rather than a guess.
 */
export function defaultAdaptFor(
  audience: readonly PortalAudienceRule[],
  accounts: Iterable<AudienceAccount>,
): InspirationAdaptFor {
  if (audience.length === 0) return 'none';
  const industries = new Set<Industry>();
  for (const a of accounts) {
    if (!PORTAL_OPERATIONAL_TYPES.includes(a.accountType)) continue;
    if (a.industry === null || !matchesAudience(audience, [a])) continue;
    industries.add(a.industry);
  }
  return industries.size === 1 ? [...industries][0]! : 'none';
}

/** Plain words for the model, per industry. ⚠️ A `Record<Industry, …>`: a fifth industry is a compile error here. Never a client or brand name. */
export const ADAPT_FOR_SETTINGS: Record<Industry, string> = {
  fitness_franchise:
    'gyms, fitness clubs and studios: members, personal trainers, classes, the gym floor and the front desk',
  automotive_franchise: 'car dealerships: the showroom, cars on the lot, the sales team and test drives',
  small_business_ecommerce:
    'small online and local retail businesses: the owner, the products, the packing bench and the customers who order',
  other: 'small local businesses: the owner and team, the shopfront or workspace, and the customers they serve',
};

export function adaptForSetting(adaptFor: InspirationAdaptFor): string | null {
  return adaptFor === 'none' ? null : ADAPT_FOR_SETTINGS[adaptFor];
}
